import { useState } from "react";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, AreaChart, Area, Legend
} from "recharts";

// ── Maghil Design Tokens ──
const T = {
  bg: "#F5F6FA",
  surface: "#FFFFFF",
  card: "#FFFFFF",
  cardBorder: "#E2E5EC",
  hover: "#F0F1F5",
  accent: "#D23A3B",
  accentSoft: "rgba(210,58,59,0.08)",
  accentGlow: "rgba(210,58,59,0.15)",
  green: "#16A34A",
  greenSoft: "rgba(22,163,74,0.08)",
  red: "#DC2626",
  redSoft: "rgba(220,38,38,0.08)",
  orange: "#D97706",
  orangeSoft: "rgba(217,119,6,0.08)",
  t1: "#1A1D2E",
  t2: "#5A6178",
  t3: "#9CA3B8",
  divider: "#E8EAF0",
  shadow: "0 1px 3px rgba(0,0,0,0.06)",
  cardShadow: "0 1px 4px rgba(0,0,0,0.05)",
  sidebarShadow: "2px 0 8px rgba(0,0,0,0.03)",
  topbarShadow: "0 1px 3px rgba(0,0,0,0.04)",
  font: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

// ── Material Symbols (loaded via Google Fonts) ──
const MI = ({ name, size = 20, color = T.t3, filled = false, style = {} }: any) => (
  <span
    className="material-symbols-rounded"
    style={{
      fontSize: size,
      color,
      fontVariationSettings: filled ? "'FILL' 1, 'wght' 400" : "'FILL' 0, 'wght' 400",
      lineHeight: 1,
      verticalAlign: "middle",
      userSelect: "none",
      ...style,
    }}
  >
    {name}
  </span>
);

// ── Mock Data ──
const revenueOverTime = [
  { date: "Mar 1", gross: 8420, net: 7180 },
  { date: "Mar 2", gross: 9150, net: 7820 },
  { date: "Mar 3", gross: 7680, net: 6490 },
  { date: "Mar 4", gross: 10230, net: 8710 },
  { date: "Mar 5", gross: 11480, net: 9820 },
  { date: "Mar 6", gross: 9870, net: 8410 },
  { date: "Mar 7", gross: 12150, net: 10380 },
  { date: "Mar 8", gross: 10640, net: 9120 },
  { date: "Mar 9", gross: 11920, net: 10180 },
];

const tenderData = [
  { name: "Credit Card", value: 62, amount: 64280, color: T.accent },
  { name: "Cash", value: 18, amount: 18650, color: "#4A90D9" },
  { name: "Debit Card", value: 12, amount: 12430, color: T.orange },
  { name: "Digital Wallet", value: 5, amount: 5180, color: T.green },
  { name: "Gift Card", value: 3, amount: 3110, color: "#8B5CF6" },
];

const channelData = [
  { channel: "Dine-in", maghil: 38200, thirdParty: 0 },
  { channel: "Instore", maghil: 22400, thirdParty: 0 },
  { channel: "Delivery", maghil: 12800, thirdParty: 18600 },
  { channel: "DoorDash", maghil: 0, thirdParty: 14200 },
  { channel: "UberEats", maghil: 0, thirdParty: 11800 },
];

const tipsData = [
  { channel: "Dine-in", tips: 4820 },
  { channel: "Pickup", tips: 1240 },
  { channel: "Delivery", tips: 2180 },
];

const refundReasons = [
  { reason: "Wrong item", count: 18, amount: 486, pct: 34 },
  { reason: "Quality issue", count: 12, amount: 324, pct: 22 },
  { reason: "Late delivery", count: 10, amount: 295, pct: 19 },
  { reason: "Customer changed mind", count: 8, amount: 198, pct: 15 },
  { reason: "Other", count: 5, amount: 112, pct: 10 },
];

const voidDetails = [
  { staff: "Maria G.", item: "Truffle Fries", reason: "Customer allergy", amount: 14.50, time: "12:34 PM" },
  { staff: "James L.", item: "Wagyu Burger", reason: "Sent to wrong table", amount: 28.00, time: "1:15 PM" },
  { staff: "Sofia R.", item: "Caesar Salad", reason: "Duplicate entry", amount: 12.00, time: "2:48 PM" },
  { staff: "David K.", item: "Craft IPA (x2)", reason: "Customer complaint", amount: 18.00, time: "5:22 PM" },
  { staff: "Maria G.", item: "Lobster Roll", reason: "Kitchen error", amount: 24.50, time: "6:10 PM" },
];

const eodData = [
  { label: "Gross Sales", value: 103650, type: "positive" },
  { label: "Sales Tax", value: -8292, type: "deduction" },
  { label: "Third-Party Tax", value: -3570, type: "deduction" },
  { label: "Discounts", value: -2180, type: "deduction" },
  { label: "Refunds", value: -1415, type: "deduction" },
  { label: "Processing Fees", value: -2074, type: "deduction" },
  { label: "Net Sales", value: 86119, type: "net" },
  { label: "Maghil Funding", value: 84200, type: "funding" },
];

const staffTipLeaderboard = [
  { name: "Maria G.", tips: 842, sales: 6840, rate: "12.3%", hours: "8.5h" },
  { name: "James L.", tips: 718, sales: 5920, rate: "12.1%", hours: "8.0h" },
  { name: "Sofia R.", tips: 634, sales: 5480, rate: "11.6%", hours: "7.5h" },
  { name: "David K.", tips: 521, sales: 4760, rate: "10.9%", hours: "8.0h" },
  { name: "Ana M.", tips: 468, sales: 4320, rate: "10.8%", hours: "6.5h" },
];

const cardTypeData = [
  { type: "Visa", amount: 32140, pct: 50 },
  { type: "Mastercard", amount: 16712, pct: 26 },
  { type: "Amex", amount: 9642, pct: 15 },
  { type: "Discover", amount: 5786, pct: 9 },
];

// ── Funds Reconciliation Data (Expected vs Processed vs Deposited) ──
const fundsData = [
  { date: "Mar 9, 2026", expected: 12150.00, processed: 12150.00, deposited: 12150.00 },
  { date: "Mar 8, 2026", expected: 10640.00, processed: 10640.00, deposited: 10640.00 },
  { date: "Mar 7, 2026", expected: 9870.00, processed: 9870.00, deposited: 9870.00 },
  { date: "Mar 6, 2026", expected: 11480.00, processed: 11324.50, deposited: 11324.50 },
  { date: "Mar 5, 2026", expected: 10230.00, processed: 10230.00, deposited: 10230.00 },
  { date: "Mar 4, 2026", expected: 7680.00, processed: 7680.00, deposited: 7680.00 },
  { date: "Mar 3, 2026", expected: 9150.00, processed: 9150.00, deposited: 8842.00 },
];

// ── Tax Reconciliation Data ──
const taxData = [
  { date: "Mar 9, 2026", taxableSales: 11232.00, computed: 926.64, collected: 926.64, diff: 0 },
  { date: "Mar 8, 2026", taxableSales: 9840.00, computed: 811.80, collected: 811.80, diff: 0 },
  { date: "Mar 7, 2026", taxableSales: 9120.50, computed: 752.44, collected: 749.12, diff: 3.32 },
  { date: "Mar 6, 2026", taxableSales: 10610.00, computed: 875.33, collected: 875.33, diff: 0 },
  { date: "Mar 5, 2026", taxableSales: 9450.00, processed: 779.63, collected: 779.63, diff: 0 },
  { date: "Mar 4, 2026", taxableSales: 7100.00, computed: 585.75, collected: 582.40, diff: 3.35 },
  { date: "Mar 3, 2026", taxableSales: 8460.00, computed: 697.95, collected: 697.95, diff: 0 },
];

// ── Tax Audit Log — order-level drill-down for mismatch dates ──
const taxAuditLog = {
  "Mar 7, 2026": [
    { orderDate: "Mar 7, 2026 11:42 AM", orderNo: "MH-40821", customer: "Walk-in", itemTotal: 68.50, computed: 5.65, collected: 5.25, paymentMode: "Credit Card", employee: "Maria G.", changeDetails: "Tax rate override applied at POS — manual 7.66% instead of 8.25%" },
    { orderDate: "Mar 7, 2026 1:18 PM", orderNo: "MH-40834", customer: "John D.", itemTotal: 124.00, computed: 10.23, collected: 10.23, paymentMode: "Credit Card", employee: "James L.", changeDetails: null },
    { orderDate: "Mar 7, 2026 6:05 PM", orderNo: "MH-40867", customer: "Sarah M.", itemTotal: 42.00, computed: 3.47, collected: 0.00, paymentMode: "Cash", employee: "Sofia R.", changeDetails: "Tax-exempt flag toggled ON after order creation — audit log shows manual override by Sofia R. at 6:08 PM" },
  ],
  "Mar 4, 2026": [
    { orderDate: "Mar 4, 2026 12:15 PM", orderNo: "MH-40612", customer: "Walk-in", itemTotal: 55.00, computed: 4.54, collected: 4.54, paymentMode: "Debit Card", employee: "David K.", changeDetails: null },
    { orderDate: "Mar 4, 2026 2:30 PM", orderNo: "MH-40638", customer: "Emily R.", itemTotal: 89.50, computed: 7.38, collected: 4.03, paymentMode: "Credit Card", employee: "Maria G.", changeDetails: "Partial tax exemption applied — customer presented resale certificate; employee applied 4.5% county-only rate instead of full 8.25%" },
    { orderDate: "Mar 4, 2026 7:45 PM", orderNo: "MH-40671", customer: "Table 12", itemTotal: 210.00, computed: 17.33, collected: 17.33, paymentMode: "Credit Card", employee: "James L.", changeDetails: null },
  ],
};

// ── Sidebar Nav Items ──
const navGroups = [
  { emoji: "📊", label: "Sales", items: ["Sales Summary", "Hourly Sales", "Order Channels"] },
  { emoji: "🍽️", label: "Menu", items: ["Item Sales", "Category Sales", "Discounts & Offers"] },
  { emoji: "👥", label: "Team", items: ["Staff Performance", "Voids & Exceptions", "Refunds"] },
  { emoji: "💰", label: "Payments", items: ["Overview"], active: true },
  { emoji: "🪑", label: "Guests", items: ["Check-In Analytics", "Party & Wait Times"] },
  { emoji: "📍", label: "Locations", items: ["Location Benchmark"] },
];

// ── Helper Components ──
const fmt = (n: any) => {
  if (Math.abs(n) >= 1000) return (n < 0 ? "-" : "") + "$" + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 0 });
  return (n < 0 ? "-" : "") + "$" + Math.abs(n).toFixed(2);
};

const DatePills = ({ active, onChange }: any) => {
  const options = ["Today", "Yesterday", "This Week", "Last 7 Days", "This Month"];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        background: T.surface,
        border: `1px solid ${T.cardBorder}`,
        borderRadius: 10,
        padding: 3,
        gap: 2,
      }}
    >
      <div style={{ padding: "0 8px", display: "flex", alignItems: "center" }}>
        <MI name="calendar_today" size={16} color={T.t3} />
      </div>
      {options.map((o) => (
        <button
          key={o}
          onClick={() => onChange(o)}
          style={{
            padding: "6px 14px",
            borderRadius: 7,
            border: "none",
            background: active === o ? T.accentSoft : "transparent",
            color: active === o ? T.accent : T.t2,
            fontSize: 12,
            fontWeight: active === o ? 600 : 400,
            fontFamily: T.font,
            cursor: "pointer",
            transition: "all 0.15s",
            whiteSpace: "nowrap",
          }}
        >
          {o}
        </button>
      ))}
    </div>
  );
};

const KPICard = ({ label, value, delta, deltaLabel, icon, iconColor, blurColor, onClick }: any) => (
  <div
    onClick={onClick}
    style={{
      flex: 1,
      minWidth: 180,
      background: T.card,
      border: `1px solid ${onClick ? "rgba(210,58,59,0.2)" : T.cardBorder}`,
      borderRadius: 14,
      padding: "20px 22px",
      boxShadow: T.cardShadow,
      position: "relative",
      overflow: "hidden",
      cursor: onClick ? "pointer" : "default",
      transition: "border-color 0.15s, box-shadow 0.15s",
    }}
  >
    {/* Drill arrow for clickable cards */}
    {onClick && (
      <svg width="10" height="10" viewBox="0 0 10 10" style={{ position: "absolute", top: 10, right: 10, opacity: 0.45 }}>
        <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
    <div
      style={{
        position: "absolute",
        top: -20,
        right: -20,
        width: 80,
        height: 80,
        borderRadius: "50%",
        background: blurColor || T.greenSoft,
        filter: "blur(20px)",
      }}
    />
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, position: "relative" }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </span>
      <MI name={icon || "payments"} size={18} color={iconColor || T.t3} />
    </div>
    <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, position: "relative" }}>{value}</div>
    {delta !== undefined && (
      <div style={{ fontSize: 12, fontWeight: 600, color: delta >= 0 ? T.green : T.red, marginTop: 4, position: "relative" }}>
        {delta >= 0 ? "↑" : "↓"} {delta >= 0 ? "+" : ""}{delta}% <span style={{ fontWeight: 400, color: T.t3 }}>{deltaLabel || "vs prior period"}</span>
      </div>
    )}
  </div>
);

const SectionCard = ({ title, subtitle, children, icon, style = {}, onClick }: any) => (
  <div
    onClick={onClick}
    style={{
      background: T.card,
      border: `1px solid ${onClick ? "rgba(210,58,59,0.2)" : T.cardBorder}`,
      borderRadius: 14,
      padding: 22,
      boxShadow: T.cardShadow,
      cursor: onClick ? "pointer" : "default",
      transition: "border-color 0.15s",
      ...style,
    }}
  >
    {title && (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.t1, display: "flex", alignItems: "center", gap: 6 }}>
            {icon && <MI name={icon} size={18} color={T.accent} />}
            {title}
          </div>
          {subtitle && <div style={{ fontSize: 11, fontWeight: 400, color: T.t3, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {onClick ? (
          <svg width="10" height="10" viewBox="0 0 10 10" style={{ opacity: 0.5 }}>
            <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <MI name="chevron_right" size={18} color={T.accent} style={{ opacity: 0.6 }} />
        )}
      </div>
    )}
    {children}
  </div>
);

const ProgressBar = ({ pct, color }: any) => (
  <div style={{ height: 6, background: T.hover, borderRadius: 3, overflow: "hidden", flex: 1 }}>
    <div style={{ height: "100%", width: `${pct}%`, background: color || T.accent, borderRadius: 3, opacity: 0.7, transition: "width 0.6s ease" }} />
  </div>
);

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: "10px 14px", boxShadow: T.cardShadow, fontFamily: T.font }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: T.t1, marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ fontSize: 11, color: T.t2, display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
          <div style={{ width: 8, height: 8, borderRadius: 2, background: p.color }} />
          {p.name}: {fmt(p.value)}
        </div>
      ))}
    </div>
  );
};

// ══════════════════════════════════════
//  MAIN DESKTOP COMPONENT
// ══════════════════════════════════════
export default function PaymentsAccountingDesktop() {
  const [dateFilter, setDateFilter] = useState("This Week");
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedGroups, setExpandedGroups] = useState({ Payments: true });
  const [sourceFilter, setSourceFilter] = useState("All");
  const [taxAuditDrawer, setTaxAuditDrawer] = useState<any>(null); // null or date string

  const toggleGroup = (label: any) => setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));

  return (
    <div style={{ fontFamily: T.font, background: T.bg, minHeight: "100vh", display: "flex", color: T.t1 }}>
      {/* ── Google Material Symbols Font ── */}
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" />

      {/* ════════ SIDEBAR (250px) ════════ */}
      <aside
        style={{
          width: 250,
          minWidth: 250,
          height: "100vh",
          position: "sticky",
          top: 0,
          background: T.surface,
          borderRight: `1px solid ${T.divider}`,
          boxShadow: T.sidebarShadow,
          display: "flex",
          flexDirection: "column",
          zIndex: 20,
          overflowY: "auto",
        }}
      >
        {/* Logo */}
        <div style={{ padding: "20px 18px", borderBottom: `1px solid ${T.divider}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background: "linear-gradient(135deg, #D23A3B, #E86B6B)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              M
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.t1 }}>Maghil</div>
              <div style={{ fontSize: 10, fontWeight: 400, color: T.t3, letterSpacing: 0.5 }}>ANALYTICS</div>
            </div>
          </div>
        </div>

        {/* Outlet Selector */}
        <div style={{ padding: "12px 14px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 10px",
              background: T.hover,
              border: `1px solid ${T.cardBorder}`,
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            <MI name="location_on" size={14} color={T.accent} filled />
            <span style={{ fontSize: 11, fontWeight: 600, color: T.t1, flex: 1 }}>Downtown Main St.</span>
            <MI name="expand_more" size={16} color={T.t3} />
          </div>
        </div>

        {/* Nav Groups */}
        <nav style={{ flex: 1, padding: "4px 8px", overflowY: "auto" }}>
          {navGroups.map((group) => (
            <div key={group.label} style={{ marginBottom: 4 }}>
              <button
                onClick={() => toggleGroup(group.label)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 10px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontFamily: T.font,
                  borderRadius: 6,
                }}
              >
                <span style={{ fontSize: 14 }}>{group.emoji}</span>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: group.active ? T.accent : T.t2, flex: 1, textAlign: "left" }}>
                  {group.label}
                </span>
                <MI
                  name="expand_more"
                  size={16}
                  color={T.t3}
                  style={{ transform: expandedGroups[group.label] ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
                />
              </button>
              {expandedGroups[group.label] && (
                <div style={{ marginLeft: 8 }}>
                  {group.items.map((item) => {
                    // Map sidebar item names to activeTab ids
                    const tabMap = {
                      "Overview": "overview",
                      "Tender Types": "tender",
                      "Tips & Charges": "tips",
                      "Refunds & Voids": "refunds",
                      "EOD Reconciliation": "eod",
                      "Channels": "channels",
                    };
                    const iconMap = {
                      "Overview": "dashboard",
                      "Tender Types": "credit_card",
                      "Tips & Charges": "volunteer_activism",
                      "Refunds & Voids": "undo",
                      "EOD Reconciliation": "account_balance",
                      "Channels": "device_hub",
                    };
                    const tabId = tabMap[item];
                    const isActive = group.active && tabId && activeTab === tabId;
                    const itemIcon = iconMap[item];
                    return (
                      <button
                        key={item}
                        onClick={() => {
                          if (tabId) setActiveTab(tabId);
                        }}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "8px 10px 8px 18px",
                          border: "none",
                          borderLeft: isActive ? `2px solid ${T.accent}` : "2px solid transparent",
                          background: isActive ? T.accentSoft : "transparent",
                          color: isActive ? T.accent : T.t2,
                          fontSize: 12,
                          fontWeight: isActive ? 600 : 400,
                          fontFamily: T.font,
                          cursor: "pointer",
                          textAlign: "left",
                          borderRadius: "0 6px 6px 0",
                          transition: "all 0.15s",
                        }}
                      >
                        {itemIcon && <MI name={itemIcon} size={15} color={isActive ? T.accent : T.t3} filled={isActive} />}
                        {item}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* User Footer */}
        <div style={{ padding: "14px 16px", borderTop: `1px solid ${T.divider}`, display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: T.accentSoft,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <MI name="person" size={16} color={T.accent} />
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>Sarah Chen</div>
            <div style={{ fontSize: 10, color: T.t3 }}>Owner</div>
          </div>
        </div>
      </aside>

      {/* ════════ MAIN CONTENT ════════ */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        {/* ── Top Bar ── */}
        <header
          style={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: T.surface,
            boxShadow: T.topbarShadow,
          }}
        >
          {/* Row 1: Breadcrumb + Title + Export */}
          <div
            style={{
              height: 56,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 24px",
              borderBottom: `1px solid ${T.divider}`,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 12, color: T.t3 }}>Reports</span>
              <MI name="chevron_right" size={14} color={T.t3} />
              <span style={{ fontSize: 12, color: T.t3 }}>Payments</span>
              <MI name="chevron_right" size={14} color={T.t3} />
              <span style={{ fontSize: 14, fontWeight: 700, color: T.t1 }}>
                {{ overview: "Overview", tender: "Tender Types", tips: "Tips & Charges", refunds: "Refunds & Voids", eod: "EOD Reconciliation", channels: "Channels" }[activeTab] || "Payments & Accounting"}
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "6px 14px",
                  background: T.surface,
                  border: `1px solid ${T.cardBorder}`,
                  borderRadius: 8,
                  color: T.t2,
                  fontSize: 11.5,
                  fontWeight: 400,
                  fontFamily: T.font,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                <MI name="download" size={15} color={T.t2} />
                Export
              </button>
            </div>
          </div>

          {/* Row 2: Filters */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "8px 24px",
              borderBottom: `1px solid ${T.divider}`,
              background: T.bg,
            }}
          >
            {/* Left: Date pills */}
            <DatePills active={dateFilter} onChange={setDateFilter} />

            {/* Right: Filter icon + Source toggle */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                background: T.surface,
                border: `1px solid ${T.cardBorder}`,
                borderRadius: 10,
                padding: 3,
                gap: 2,
              }}
            >
              <div style={{ padding: "0 8px", display: "flex", alignItems: "center" }}>
                <MI name="filter_alt" size={16} color={T.t3} />
              </div>
              {["All", "Maghil", "Third Party"].map((s) => (
                <button
                  key={s}
                  onClick={() => setSourceFilter(s)}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 7,
                    border: "none",
                    background: sourceFilter === s ? T.accentSoft : "transparent",
                    color: sourceFilter === s ? T.accent : T.t2,
                    fontSize: 12,
                    fontWeight: sourceFilter === s ? 600 : 400,
                    fontFamily: T.font,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    whiteSpace: "nowrap",
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        </header>

        {/* ── Scrollable Content ── */}
        <main style={{ flex: 1, overflowY: "auto", padding: 24 }}>

          {/* ════════════════════════════════════════ */}
          {/* OVERVIEW TAB */}
          {/* ════════════════════════════════════════ */}
          {activeTab === "overview" && (
            <>
              {/* KPI Row */}
              <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                <KPICard label="Gross Sales" value="$103,650" delta={8.4} icon="trending_up" iconColor={T.green} blurColor={T.greenSoft} />
                <KPICard label="Net Sales" value="$86,119" delta={6.2} icon="account_balance_wallet" iconColor={T.accent} blurColor={T.accentSoft} />
                <KPICard label="Total Refunds" value="$1,415" delta={-12.4} icon="undo" iconColor={T.red} blurColor={T.redSoft} deltaLabel="vs prior period" onClick={() => setActiveTab("refunds")} />
                <KPICard label="Tips Collected" value="$8,240" delta={5.7} icon="volunteer_activism" iconColor={T.green} blurColor={T.greenSoft} onClick={() => setActiveTab("tips")} />
              </div>

              {/* Revenue Over Time Chart */}
              <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                <SectionCard title="Revenue Trend" subtitle="Gross vs Net Sales" icon="show_chart" style={{ flex: 2 }}>
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={revenueOverTime} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="grossGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={T.accent} stopOpacity={0.15} />
                          <stop offset="100%" stopColor={T.accent} stopOpacity={0.01} />
                        </linearGradient>
                        <linearGradient id="netGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={T.green} stopOpacity={0.12} />
                          <stop offset="100%" stopColor={T.green} stopOpacity={0.01} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: T.t3 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: T.t3 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="gross" stroke={T.accent} strokeWidth={2.5} fill="url(#grossGrad)" name="Gross" />
                      <Area type="monotone" dataKey="net" stroke={T.green} strokeWidth={2} strokeDasharray="4 2" fill="url(#netGrad)" name="Net" />
                    </AreaChart>
                  </ResponsiveContainer>
                </SectionCard>

                <SectionCard title="Tender Mix" subtitle="Revenue by payment method" icon="pie_chart" style={{ flex: 1 }} onClick={() => setActiveTab("tender")}>
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <ResponsiveContainer width={140} height={140}>
                      <PieChart>
                        <Pie data={tenderData} cx="50%" cy="50%" innerRadius={42} outerRadius={58} dataKey="value" strokeWidth={0}>
                          {tenderData.map((d, i) => (
                            <Cell key={i} fill={d.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ flex: 1 }}>
                      {tenderData.map((d) => (
                        <div key={d.name} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                          <div style={{ width: 8, height: 8, borderRadius: 2, background: d.color, flexShrink: 0 }} />
                          <span style={{ fontSize: 11, color: T.t2, width: 80 }}>{d.name}</span>
                          <span style={{ fontSize: 11, fontWeight: 600, color: T.t1 }}>{d.value}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </SectionCard>
              </div>

              {/* Channel Revenue */}
              <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                <SectionCard title="Channel Revenue" subtitle="Maghil vs Third-Party by channel" icon="device_hub" style={{ flex: 1 }} onClick={() => setActiveTab("channels")}>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={channelData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} />
                      <XAxis dataKey="channel" tick={{ fontSize: 10, fill: T.t3 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: T.t3 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="maghil" name="Maghil" fill={T.accent} radius={[4, 4, 0, 0]} maxBarSize={24} />
                      <Bar dataKey="thirdParty" name="Third Party" fill={T.orange} radius={[4, 4, 0, 0]} maxBarSize={24} />
                    </BarChart>
                  </ResponsiveContainer>
                </SectionCard>
              </div>

              {/* ── Funds Reconciliation Table ── */}
              <SectionCard
                title="Funds"
                subtitle="Expected vs Processed vs Deposited"
                icon="account_balance"
                style={{ marginBottom: 24 }}
              >
                {/* Header label row */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 10, color: T.t3 }}>
                      Last synced: Mar 9, 2026 2:15 PM
                    </span>
                    <MI name="sync" size={13} color={T.t3} />
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: T.surface, fontSize: 10, fontFamily: T.font, color: T.t2, cursor: "pointer" }}>
                      <MI name="download" size={12} color={T.t3} /> CSV
                    </button>
                    <button style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: T.surface, fontSize: 10, fontFamily: T.font, color: T.t2, cursor: "pointer" }}>
                      <MI name="picture_as_pdf" size={12} color={T.t3} /> PDF
                    </button>
                  </div>
                </div>
                {/* Column group header */}
                <div style={{ display: "flex", borderBottom: `2px solid ${T.divider}` }}>
                  <span style={{ width: 130, fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase", padding: "8px 0" }}>Date</span>
                  <div style={{ flex: 1, textAlign: "center", borderBottom: `2px solid ${T.accent}`, marginBottom: -2 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: T.t1, padding: "6px 0", display: "inline-block" }}>Funding</span>
                  </div>
                </div>
                {/* Sub-header */}
                <div style={{ display: "flex", padding: "8px 0", borderBottom: `1px solid ${T.divider}` }}>
                  <span style={{ width: 130, fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase" }}>Date</span>
                  <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase", textAlign: "right" }}>Expected Funds</span>
                  <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase", textAlign: "right" }}>Processed Funds</span>
                  <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase", textAlign: "right" }}>Deposited Funds</span>
                </div>
                {/* Data rows */}
                {fundsData.map((row, i) => {
                  const procMismatch = row.expected !== row.processed;
                  const depMismatch = row.processed !== row.deposited;
                  return (
                    <div
                      key={row.date}
                      style={{
                        display: "flex",
                        padding: "10px 0",
                        borderBottom: `1px solid ${T.divider}`,
                        alignItems: "center",
                        cursor: "pointer",
                        transition: "background 0.1s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <span style={{ width: 130, fontSize: 12, fontWeight: 500, color: T.t1 }}>{row.date}</span>
                      <span style={{ flex: 1, fontSize: 12, color: T.t1, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        {row.expected.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </span>
                      <span
                        style={{
                          flex: 1,
                          fontSize: 12,
                          fontWeight: procMismatch ? 600 : 400,
                          color: procMismatch ? T.orange : T.t1,
                          background: procMismatch ? T.orangeSoft : "transparent",
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                          padding: procMismatch ? "2px 8px" : 0,
                          borderRadius: 4,
                          display: "inline-flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        {procMismatch && <MI name="warning" size={12} color={T.orange} />}
                        {row.processed.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </span>
                      <span
                        style={{
                          flex: 1,
                          fontSize: 12,
                          fontWeight: depMismatch ? 600 : 400,
                          color: depMismatch ? T.red : T.t1,
                          background: depMismatch ? T.redSoft : "transparent",
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                          padding: depMismatch ? "2px 8px" : 0,
                          borderRadius: 4,
                          display: "inline-flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        {depMismatch && <MI name="error" size={12} color={T.red} />}
                        {row.deposited.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  );
                })}
                {/* Sum row */}
                <div style={{ display: "flex", padding: "10px 0", alignItems: "center", background: T.hover, borderRadius: 6, marginTop: 4 }}>
                  <span style={{ width: 130, fontSize: 12, fontWeight: 700, color: T.t1, paddingLeft: 8 }}>Sum =</span>
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: T.t1, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {fundsData.reduce((s, r) => s + r.expected, 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </span>
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: T.t1, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {fundsData.reduce((s, r) => s + r.processed, 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </span>
                  <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: T.t1, textAlign: "right", fontVariantNumeric: "tabular-nums", paddingRight: 8 }}>
                    {fundsData.reduce((s, r) => s + r.deposited, 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </span>
                </div>
                {/* Pending Refunds toggle note */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, padding: "8px 12px", background: T.orangeSoft, borderRadius: 8, border: `1px solid rgba(217,119,6,0.15)` }}>
                  <MI name="info" size={14} color={T.orange} />
                  <span style={{ fontSize: 11, color: T.orange, fontWeight: 500 }}>
                    1 pending refund ($155.50) may explain the Expected vs Processed variance on Mar 6.
                  </span>
                  <button style={{ marginLeft: "auto", fontSize: 10, fontWeight: 600, color: T.accent, background: "transparent", border: "none", cursor: "pointer", fontFamily: T.font, textDecoration: "underline" }}>
                    View Pending Refunds
                  </button>
                </div>
              </SectionCard>

              {/* ── Tax Reconciliation Table ── */}
              <SectionCard
                title="Taxes"
                subtitle="Computed vs Collected tax reconciliation"
                icon="receipt_long"
                style={{ marginBottom: 24 }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                  <span style={{ fontSize: 10, color: T.t3 }}>
                    Rounding: half-up to nearest $0.01
                  </span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: T.surface, fontSize: 10, fontFamily: T.font, color: T.t2, cursor: "pointer" }}>
                      <MI name="download" size={12} color={T.t3} /> CSV
                    </button>
                    <button style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 10px", borderRadius: 6, border: `1px solid ${T.cardBorder}`, background: T.surface, fontSize: 10, fontFamily: T.font, color: T.t2, cursor: "pointer" }}>
                      <MI name="picture_as_pdf" size={12} color={T.t3} /> PDF
                    </button>
                  </div>
                </div>
                {/* Header */}
                <div style={{ display: "flex", padding: "8px 0", borderBottom: `2px solid ${T.divider}` }}>
                  <span style={{ width: 130, fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase" }}>Date</span>
                  <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase", textAlign: "right" }}>Taxable Sales</span>
                  <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase", textAlign: "right" }}>Computed Tax</span>
                  <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase", textAlign: "right" }}>Collected Tax</span>
                  <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase", textAlign: "right" }}>Difference</span>
                </div>
                {/* Data rows */}
                {taxData.map((row) => {
                  const hasDiff = row.diff !== 0;
                  const showAnalyse = row.diff > 0.05;
                  return (
                    <div
                      key={row.date}
                      style={{
                        display: "flex",
                        padding: "10px 0",
                        borderBottom: `1px solid ${T.divider}`,
                        alignItems: "center",
                        transition: "background 0.1s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <span style={{ width: 130, fontSize: 12, fontWeight: 500, color: T.t1 }}>{row.date}</span>
                      <span style={{ flex: 1, fontSize: 12, color: T.t1, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        ${row.taxableSales.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </span>
                      <span style={{ flex: 1, fontSize: 12, color: T.t1, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        ${(row.computed || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </span>
                      <span style={{ flex: 1, fontSize: 12, color: T.t1, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        ${row.collected.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </span>
                      <span
                        style={{
                          flex: 1,
                          fontSize: 12,
                          fontWeight: hasDiff ? 600 : 400,
                          color: hasDiff ? T.red : T.green,
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                          display: "inline-flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        {hasDiff && <MI name="warning" size={12} color={T.red} />}
                        {hasDiff ? `-$${row.diff.toFixed(2)}` : "$0.00"}
                        {showAnalyse && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setTaxAuditDrawer(row.date); }}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 3,
                              padding: "3px 8px",
                              borderRadius: 5,
                              border: `1px solid rgba(220,38,38,0.2)`,
                              background: T.redSoft,
                              color: T.red,
                              fontSize: 10,
                              fontWeight: 600,
                              fontFamily: T.font,
                              cursor: "pointer",
                              marginLeft: 4,
                              transition: "all 0.15s",
                            }}
                          >
                            <MI name="search" size={11} color={T.red} />
                            Analyse
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })}
                {/* Sum row */}
                {(() => {
                  const totals = taxData.reduce(
                    (acc, r) => ({
                      taxable: acc.taxable + r.taxableSales,
                      computed: acc.computed + (r.computed || 0),
                      collected: acc.collected + r.collected,
                      diff: acc.diff + r.diff,
                    }),
                    { taxable: 0, computed: 0, collected: 0, diff: 0 }
                  );
                  const hasDiff = Math.abs(totals.diff) > 0.005;
                  return (
                    <div style={{ display: "flex", padding: "10px 0", alignItems: "center", background: T.hover, borderRadius: 6, marginTop: 4 }}>
                      <span style={{ width: 130, fontSize: 12, fontWeight: 700, color: T.t1, paddingLeft: 8 }}>Sum =</span>
                      <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: T.t1, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        ${totals.taxable.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </span>
                      <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: T.t1, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        ${totals.computed.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </span>
                      <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: T.t1, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        ${totals.collected.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </span>
                      <span style={{ flex: 1, fontSize: 12, fontWeight: 700, color: hasDiff ? T.red : T.green, textAlign: "right", fontVariantNumeric: "tabular-nums", paddingRight: 8 }}>
                        {hasDiff ? `-$${totals.diff.toFixed(2)}` : "$0.00"}
                      </span>
                    </div>
                  );
                })()}
              </SectionCard>

              {/* ── EOD Reconciliation Section ── */}
              <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                {/* Waterfall */}
                <SectionCard title="Sales to Funding Waterfall" subtitle="From gross to deposit" icon="waterfall_chart" style={{ flex: 2 }}>
                  <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                    {eodData.map((item, i) => {
                      const maxVal = 103650;
                      const pct = Math.abs(item.value) / maxVal * 100;
                      const isNet = item.type === "net";
                      const isFunding = item.type === "funding";
                      const barColor = isNet ? T.green : isFunding ? T.accent : item.type === "positive" ? T.green : T.red;
                      return (
                        <div
                          key={item.label}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "8px 0",
                            borderBottom: i < eodData.length - 1 ? `1px solid ${T.divider}` : "none",
                            background: isNet || isFunding ? T.accentSoft : "transparent",
                            borderRadius: isNet || isFunding ? 6 : 0,
                            paddingLeft: isNet || isFunding ? 8 : 0,
                            marginTop: isNet ? 4 : 0,
                          }}
                        >
                          <span style={{ width: 140, fontSize: 12, fontWeight: isNet || isFunding ? 600 : 400, color: T.t2 }}>{item.label}</span>
                          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{ height: 8, width: `${pct}%`, background: barColor, borderRadius: 4, opacity: isNet || isFunding ? 1 : 0.6, transition: "width 0.6s ease" }} />
                          </div>
                          <span style={{ width: 100, textAlign: "right", fontSize: 13, fontWeight: 600, color: item.value < 0 ? T.red : T.t1 }}>
                            {item.value < 0 ? "-" : ""}${Math.abs(item.value).toLocaleString()}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </SectionCard>

                {/* Third-Party Reconciliation */}
                <SectionCard title="Third-Party Reconciliation" subtitle="Per-platform gross vs order total" icon="compare_arrows" style={{ flex: 1 }}>
                  {[
                    { platform: "UberEats", gross: 11800, orderTotal: 12450, icon: "delivery_dining" },
                    { platform: "DoorDash", gross: 14200, orderTotal: 14180, icon: "two_wheeler" },
                    { platform: "GrubHub", gross: 8400, orderTotal: 8520, icon: "takeout_dining" },
                  ].map((p) => {
                    const variance = p.gross - p.orderTotal;
                    return (
                      <div key={p.platform} style={{ padding: "12px 0", borderBottom: `1px solid ${T.divider}` }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <MI name={p.icon} size={16} color={T.accent} />
                            <span style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>{p.platform}</span>
                          </div>
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: Math.abs(variance) < 50 ? T.green : T.orange,
                              background: Math.abs(variance) < 50 ? T.greenSoft : T.orangeSoft,
                              padding: "2px 6px",
                              borderRadius: 4,
                            }}
                          >
                            {variance >= 0 ? "+" : ""}{fmt(variance)}
                          </span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.t3 }}>
                          <span>Gross: {fmt(p.gross)}</span>
                          <span>Order Total: {fmt(p.orderTotal)}</span>
                        </div>
                      </div>
                    );
                  })}
                </SectionCard>
              </div>
            </>
          )}

          {/* ════════════════════════════════════════ */}
          {/* TENDER TYPES TAB */}
          {/* ════════════════════════════════════════ */}
          {activeTab === "tender" && (
            <>
              <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                <KPICard label="Total Collected" value="$103,650" delta={8.4} icon="point_of_sale" blurColor={T.greenSoft} />
                <KPICard label="Credit Card" value="$64,280" delta={9.1} icon="credit_card" iconColor={T.accent} blurColor={T.accentSoft} />
                <KPICard label="Cash" value="$18,650" delta={-2.3} icon="payments" iconColor={T.green} blurColor={T.greenSoft} />
                <KPICard label="Digital Wallet" value="$5,180" delta={22.5} icon="phone_iphone" iconColor={T.orange} blurColor={T.orangeSoft} />
              </div>
              <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                <SectionCard title="Tender Type Breakdown" subtitle="Detailed sales by payment method" icon="donut_large" style={{ flex: 2 }}>
                  {/* Table */}
                  <div>
                    <div style={{ display: "flex", padding: "8px 0", borderBottom: `1px solid ${T.divider}` }}>
                      {["Tender Type", "Orders", "Total Sales", "Avg Ticket", "% of Total"].map((h) => (
                        <span key={h} style={{ flex: 1, fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase" }}>{h}</span>
                      ))}
                    </div>
                    {[
                      { type: "Credit Card", orders: 1842, total: "$64,280", avg: "$34.90", pct: "62%" },
                      { type: "Cash", orders: 624, total: "$18,650", avg: "$29.89", pct: "18%" },
                      { type: "Debit Card", orders: 389, total: "$12,430", avg: "$31.95", pct: "12%" },
                      { type: "Digital Wallet", orders: 168, total: "$5,180", avg: "$30.83", pct: "5%" },
                      { type: "Gift Card", orders: 97, total: "$3,110", avg: "$32.06", pct: "3%" },
                    ].map((row, i) => (
                      <div
                        key={row.type}
                        style={{
                          display: "flex",
                          padding: "10px 0",
                          borderBottom: `1px solid ${T.divider}`,
                          alignItems: "center",
                          cursor: "pointer",
                          transition: "background 0.1s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: T.t1 }}>{row.type}</span>
                        <span style={{ flex: 1, fontSize: 12, color: T.t1 }}>{row.orders.toLocaleString()}</span>
                        <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: T.t1 }}>{row.total}</span>
                        <span style={{ flex: 1, fontSize: 12, color: T.t2 }}>{row.avg}</span>
                        <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: T.accent }}>{row.pct}</span>
                      </div>
                    ))}
                  </div>
                </SectionCard>
                <SectionCard title="Card Type Distribution" subtitle="Breakdown by card network" icon="credit_score" style={{ flex: 1 }}>
                  {cardTypeData.map((card) => (
                    <div key={card.type} style={{ marginBottom: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: T.t2 }}>{card.type}</span>
                        <span style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>${card.amount.toLocaleString()}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <ProgressBar pct={card.pct} color={T.accent} />
                        <span style={{ fontSize: 10, color: T.t3, minWidth: 28 }}>{card.pct}%</span>
                      </div>
                    </div>
                  ))}
                </SectionCard>
              </div>
            </>
          )}

          {/* ════════════════════════════════════════ */}
          {/* TIPS TAB */}
          {/* ════════════════════════════════════════ */}
          {activeTab === "tips" && (
            <>
              <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                <KPICard label="Total Tips" value="$8,240" delta={5.7} icon="volunteer_activism" iconColor={T.green} blurColor={T.greenSoft} />
                <KPICard label="Tip Rate" value="8.0%" delta={0.3} icon="percent" iconColor={T.accent} blurColor={T.accentSoft} deltaLabel="pts vs prior" />
                <KPICard label="Service Charges" value="$1,860" delta={12.1} icon="room_service" iconColor={T.orange} blurColor={T.orangeSoft} />
                <KPICard label="Gratuity (Auto)" value="$420" delta={-4.2} icon="auto_awesome" iconColor={T.t3} blurColor={T.hover} />
              </div>
              <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                <SectionCard title="Tips by Channel" subtitle="Breakdown across order types" icon="stacked_bar_chart" style={{ flex: 1 }}>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={tipsData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.divider} horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 10, fill: T.t3 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(1)}k`} />
                      <YAxis type="category" dataKey="channel" tick={{ fontSize: 11, fill: T.t2 }} axisLine={false} tickLine={false} width={60} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="tips" name="Tips" fill={T.green} radius={[0, 4, 4, 0]} maxBarSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                </SectionCard>
                <SectionCard title="Staff Tip Leaderboard" subtitle="Top earners this period" icon="leaderboard" style={{ flex: 1.5 }}>
                  <div>
                    <div style={{ display: "flex", padding: "6px 0", borderBottom: `1px solid ${T.divider}` }}>
                      {["Staff", "Tips", "Sales", "Tip Rate", "Hours"].map((h) => (
                        <span key={h} style={{ flex: 1, fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase" }}>{h}</span>
                      ))}
                    </div>
                    {staffTipLeaderboard.map((s, i) => (
                      <div key={s.name} style={{ display: "flex", padding: "10px 0", borderBottom: `1px solid ${T.divider}`, alignItems: "center" }}>
                        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                          <div
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: 4,
                              background: i === 0 ? T.greenSoft : T.hover,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 10,
                              fontWeight: 700,
                              color: i === 0 ? T.green : T.t3,
                            }}
                          >
                            {i + 1}
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: T.accent }}>{s.name}</span>
                        </div>
                        <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: T.green }}>${s.tips}</span>
                        <span style={{ flex: 1, fontSize: 12, color: T.t1 }}>${s.sales.toLocaleString()}</span>
                        <span style={{ flex: 1, fontSize: 12, color: T.t2 }}>{s.rate}</span>
                        <span style={{ flex: 1, fontSize: 12, color: T.t3 }}>{s.hours}</span>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>
            </>
          )}

          {/* ════════════════════════════════════════ */}
          {/* REFUNDS & VOIDS TAB */}
          {/* ════════════════════════════════════════ */}
          {activeTab === "refunds" && (
            <>
              <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                <KPICard label="Total Refunds" value="$1,415" delta={-12.4} icon="undo" iconColor={T.red} blurColor={T.redSoft} />
                <KPICard label="Refund Rate" value="1.4%" delta={-0.2} icon="percent" iconColor={T.green} blurColor={T.greenSoft} deltaLabel="pts (improving)" />
                <KPICard label="Total Voids" value="$97.00" delta={8.2} icon="block" iconColor={T.orange} blurColor={T.orangeSoft} />
                <KPICard label="Cancelled Orders" value="$680" delta={-5.1} icon="cancel" iconColor={T.red} blurColor={T.redSoft} />
              </div>
              <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                <SectionCard title="Refund Reasons" subtitle="Breakdown by cancellation reason" icon="help_outline" style={{ flex: 1 }}>
                  {refundReasons.map((r) => (
                    <div key={r.reason} style={{ marginBottom: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: T.t2 }}>{r.reason}</span>
                        <div style={{ display: "flex", gap: 12 }}>
                          <span style={{ fontSize: 11, color: T.t3 }}>{r.count} orders</span>
                          <span style={{ fontSize: 11, fontWeight: 600, color: T.t1 }}>${r.amount}</span>
                        </div>
                      </div>
                      <ProgressBar pct={r.pct} color={T.red} />
                    </div>
                  ))}
                </SectionCard>
                <SectionCard title="Void Detail Log" subtitle="Recent voids with staff attribution" icon="list_alt" style={{ flex: 1.5 }}>
                  <div>
                    <div style={{ display: "flex", padding: "6px 0", borderBottom: `1px solid ${T.divider}` }}>
                      {["Staff", "Item", "Reason", "Amount", "Time"].map((h) => (
                        <span key={h} style={{ flex: 1, fontSize: 11, fontWeight: 600, color: T.t3, textTransform: "uppercase" }}>{h}</span>
                      ))}
                    </div>
                    {voidDetails.map((v, i) => (
                      <div key={i} style={{ display: "flex", padding: "10px 0", borderBottom: `1px solid ${T.divider}`, alignItems: "center" }}>
                        <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: T.accent }}>{v.staff}</span>
                        <span style={{ flex: 1, fontSize: 12, color: T.t1 }}>{v.item}</span>
                        <span style={{ flex: 1, fontSize: 11, color: T.t2 }}>{v.reason}</span>
                        <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: T.orange }}>${v.amount.toFixed(2)}</span>
                        <span style={{ flex: 1, fontSize: 11, color: T.t3 }}>{v.time}</span>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>
            </>
          )}

          {/* ════════════════════════════════════════ */}
          {/* CHANNELS TAB */}
          {/* ════════════════════════════════════════ */}
          {activeTab === "channels" && (
            <>
              <div style={{ display: "flex", gap: 16, marginBottom: 24 }}>
                <KPICard label="Maghil Revenue" value="$73,400" delta={6.8} icon="storefront" iconColor={T.accent} blurColor={T.accentSoft} />
                <KPICard label="Third-Party Revenue" value="$34,400" delta={12.3} icon="local_shipping" iconColor={T.orange} blurColor={T.orangeSoft} />
                <KPICard label="Maghil Share" value="68.1%" delta={-1.4} icon="pie_chart" iconColor={T.t3} blurColor={T.hover} deltaLabel="pts shift to 3P" />
              </div>
              <div style={{ display: "flex", gap: 16 }}>
                <SectionCard title="Revenue by Channel" subtitle="Stacked comparison" icon="bar_chart" style={{ flex: 2 }}>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={channelData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={T.divider} vertical={false} />
                      <XAxis dataKey="channel" tick={{ fontSize: 10, fill: T.t3 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: T.t3 }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 11, color: T.t2, paddingTop: 8 }} />
                      <Bar dataKey="maghil" name="Maghil" stackId="a" fill={T.accent} radius={[0, 0, 0, 0]} maxBarSize={28} />
                      <Bar dataKey="thirdParty" name="Third Party" stackId="a" fill={T.orange} radius={[4, 4, 0, 0]} maxBarSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                </SectionCard>
                <SectionCard title="Maghil vs Third-Party" icon="donut_large" style={{ flex: 1 }}>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={[
                          { name: "Maghil", value: 73400, color: T.accent },
                          { name: "Third Party", value: 34400, color: T.orange },
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={70}
                        dataKey="value"
                        strokeWidth={0}
                      >
                        <Cell fill={T.accent} />
                        <Cell fill={T.orange} />
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ display: "flex", justifyContent: "center", gap: 20, marginTop: 8 }}>
                    {[
                      { label: "Maghil", color: T.accent, value: "$73.4k" },
                      { label: "Third Party", color: T.orange, value: "$34.4k" },
                    ].map((item) => (
                      <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 8, height: 8, borderRadius: 2, background: item.color }} />
                        <span style={{ fontSize: 11, color: T.t2 }}>{item.label}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: T.t1 }}>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>
            </>
          )}
        </main>
      </div>

      {/* ════════ TAX DISCREPANCY AUDIT LOG DRAWER ════════ */}
      {taxAuditDrawer && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setTaxAuditDrawer(null)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(26,29,46,0.35)",
              zIndex: 90,
              transition: "opacity 0.2s",
            }}
          />
          {/* Drawer */}
          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              width: 680,
              height: "100vh",
              background: T.surface,
              boxShadow: "-8px 0 30px rgba(0,0,0,0.12)",
              zIndex: 100,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Drawer Header */}
            <div
              style={{
                padding: "20px 24px",
                borderBottom: `1px solid ${T.divider}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <MI name="policy" size={20} color={T.accent} />
                  <span style={{ fontSize: 16, fontWeight: 700, color: T.t1 }}>Tax Discrepancy Audit Log</span>
                </div>
                <div style={{ fontSize: 12, color: T.t3, display: "flex", alignItems: "center", gap: 6 }}>
                  <MI name="calendar_today" size={12} color={T.t3} />
                  {taxAuditDrawer}
                  <span style={{ margin: "0 4px", color: T.divider }}>|</span>
                  <span style={{ color: T.red, fontWeight: 600 }}>
                    Variance: -${taxData.find(r => r.date === taxAuditDrawer)?.diff.toFixed(2) || "0.00"}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setTaxAuditDrawer(null)}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  border: `1px solid ${T.cardBorder}`,
                  background: T.surface,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <MI name="close" size={18} color={T.t2} />
              </button>
            </div>

            {/* Drawer Content — Scrollable */}
            <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
              {/* Summary banner */}
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  marginBottom: 20,
                  padding: "14px 16px",
                  background: T.redSoft,
                  borderRadius: 10,
                  border: `1px solid rgba(220,38,38,0.12)`,
                  alignItems: "center",
                }}
              >
                <MI name="warning" size={18} color={T.red} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.red, marginBottom: 2 }}>
                    {(taxAuditLog[taxAuditDrawer] || []).filter(o => o.changeDetails).length} order(s) with tax discrepancies detected
                  </div>
                  <div style={{ fontSize: 11, color: T.t2 }}>
                    Orders where computed tax differs from collected tax are highlighted below. The "Tax Change Details" column shows root cause from the tax audit log.
                  </div>
                </div>
              </div>

              {/* Orders Table */}
              <div
                style={{
                  background: T.card,
                  border: `1px solid ${T.cardBorder}`,
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              >
                {/* Table Header */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "100px 80px 80px 72px 72px 72px 78px 80px 1fr",
                    gap: 0,
                    padding: "10px 14px",
                    background: T.hover,
                    borderBottom: `1px solid ${T.divider}`,
                  }}
                >
                  {["Order date", "Order no.", "Customer name", "Item total", "Computed tax", "Collected tax", "Payment mode", "Employee name", "Tax change details"].map((h) => (
                    <span key={h} style={{ fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", lineHeight: 1.3 }}>{h}</span>
                  ))}
                </div>

                {/* Table Rows */}
                {(taxAuditLog[taxAuditDrawer] || []).map((order, i) => {
                  const hasMismatch = order.computed !== order.collected;
                  return (
                    <div
                      key={order.orderNo}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "100px 80px 80px 72px 72px 72px 78px 80px 1fr",
                        gap: 0,
                        padding: "12px 14px",
                        borderBottom: i < (taxAuditLog[taxAuditDrawer] || []).length - 1 ? `1px solid ${T.divider}` : "none",
                        background: hasMismatch ? "rgba(220,38,38,0.03)" : "transparent",
                        alignItems: "flex-start",
                        transition: "background 0.1s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = hasMismatch ? "rgba(220,38,38,0.06)" : T.hover)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = hasMismatch ? "rgba(220,38,38,0.03)" : "transparent")}
                    >
                      <span style={{ fontSize: 11, color: T.t1, fontVariantNumeric: "tabular-nums" }}>{order.orderDate.split(", 2026 ")[1] || order.orderDate}</span>
                      <span style={{ fontSize: 11, fontWeight: 500, color: T.accent }}>{order.orderNo}</span>
                      <span style={{ fontSize: 11, color: T.t2 }}>{order.customer}</span>
                      <span style={{ fontSize: 11, color: T.t1, fontVariantNumeric: "tabular-nums" }}>${order.itemTotal.toFixed(2)}</span>
                      <span style={{ fontSize: 11, color: T.t1, fontVariantNumeric: "tabular-nums" }}>${order.computed.toFixed(2)}</span>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: hasMismatch ? 600 : 400,
                          color: hasMismatch ? T.red : T.t1,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        ${order.collected.toFixed(2)}
                      </span>
                      <span style={{ fontSize: 11, color: T.t2 }}>{order.paymentMode}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: T.accent }}>{order.employee}</span>
                      <div>
                        {order.changeDetails ? (
                          <div
                            style={{
                              fontSize: 10,
                              color: T.orange,
                              background: T.orangeSoft,
                              padding: "6px 8px",
                              borderRadius: 6,
                              lineHeight: 1.45,
                              border: `1px solid rgba(217,119,6,0.12)`,
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 3 }}>
                              <MI name="edit_note" size={12} color={T.orange} />
                              <span style={{ fontWeight: 600, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.3 }}>From the tax audit log</span>
                            </div>
                            {order.changeDetails}
                          </div>
                        ) : (
                          <span style={{ fontSize: 10, color: T.t3, fontStyle: "italic" }}>No changes</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Drawer Footer */}
            <div
              style={{
                padding: "14px 24px",
                borderTop: `1px solid ${T.divider}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
                background: T.bg,
              }}
            >
              <span style={{ fontSize: 11, color: T.t3 }}>
                Showing {(taxAuditLog[taxAuditDrawer] || []).length} orders for {taxAuditDrawer}
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 14px", borderRadius: 8, border: `1px solid ${T.cardBorder}`, background: T.surface, fontSize: 11, fontFamily: T.font, color: T.t2, cursor: "pointer" }}>
                  <MI name="download" size={14} color={T.t3} /> Export CSV
                </button>
                <button
                  onClick={() => setTaxAuditDrawer(null)}
                  style={{
                    padding: "6px 16px",
                    borderRadius: 8,
                    border: "none",
                    background: T.accent,
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 600,
                    fontFamily: T.font,
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
